import { ReactComponent as ChevronDown } from "assets/svg/outline-chevron-down-16.svg";
import { ReactComponent as OutlineChevronLeft } from "assets/svg/outline-chevron-left-16.svg";
import { ReactComponent as OutlineQuestionCircleSmall } from "assets/svg/outline-question-circle-small.svg";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
import ConfirmModal from "components/confirm-modal";
import ErrorMessage from "components/error-message";
import IconButton from "components/icon-button";
import OverlayTrigger from "components/overlay-trigger/OverlayTrigger";
import RadioButton from "components/radio-button";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  DropdownButton,
  FormControl,
  InputGroup,
  Tooltip,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import postOrchidPro from "services/postOrchidPro";
import { hideMobileSidebar } from "store/calendarSidebarSlice";
import styles from "./CancellationPolicyMobileScreen.module.scss";

const NOTICE_HOURS_OPTIONS = [24, 48, 72, 96];

const DEFAULT_NOTICE_HOURS = 24;
const DEFAULT_FEE_TYPE = "percent";
const DEFAULT_PERCENT_FEE = 20;
const DEFAULT_FLAT_FEE = 10;

const getNoticeHoursOptionText = (hours: number) => `Less than ${hours} hours`;

const getFeeType = (orchidPro?: OrchidPro) => {
  if (orchidPro?.cancellation_policy_flat_fee) {
    return "flat";
  }

  if (orchidPro?.cancellation_policy_percent_fee) {
    return "percent";
  }

  return DEFAULT_FEE_TYPE;
};

const CancellationPolicyMobileScreen = () => {
  const dispatch = useDispatch();

  const { orchidPro, setOrchidPro } = useOrchidPro();

  const [error, setError] = useState<any>();

  const [isCancellationPolicyActive, setIsCancellationPolicyActive] =
    useState(false);

  const [cancellationNoticeHours, setCancellationNoticeHours] =
    useState<number>(DEFAULT_NOTICE_HOURS);
  const [feeType, setFeeType] = useState<"flat" | "percent">(() =>
    getFeeType(orchidPro),
  );
  const [flatFee, setFlatFee] = useState<number>(DEFAULT_FLAT_FEE);
  const [percentFee, setPercentFee] = useState<number>(DEFAULT_PERCENT_FEE);

  useEffect(() => {
    if (orchidPro) {
      setIsCancellationPolicyActive(
        orchidPro.cancellation_policy_flat_fee !== null ||
          orchidPro.cancellation_policy_percent_fee !== null,
      );
      setCancellationNoticeHours(
        orchidPro.cancellation_notice_hours ?? DEFAULT_NOTICE_HOURS,
      );
      setFeeType(getFeeType(orchidPro));
      setFlatFee(orchidPro.cancellation_policy_flat_fee ?? DEFAULT_FLAT_FEE);
      setPercentFee(
        orchidPro.cancellation_policy_percent_fee ?? DEFAULT_PERCENT_FEE,
      );
    }
  }, [orchidPro]);

  const {
    show: showConfirm,
    open: openConfirm,
    close: closeConfirm,
  } = useToggleModal(false);

  const handleApply = async () => {
    const cancellationPolicy = isCancellationPolicyActive
      ? {
          clear_cancellation_policy: false,
          cancellation_notice_hours: cancellationNoticeHours,
          cancellation_policy_flat_fee: feeType === "flat" ? flatFee : null,
          cancellation_policy_percent_fee:
            feeType === "percent" ? percentFee : null,
        }
      : {
          clear_cancellation_policy: true,
          cancellation_notice_hours: null,
          cancellation_policy_flat_fee: null,
          cancellation_policy_percent_fee: null,
        };
    try {
      const updatedOrchidPro = await postOrchidPro(cancellationPolicy);
      setOrchidPro(updatedOrchidPro);
      dispatch(hideMobileSidebar());
    } catch (error) {
      closeConfirm();
      setError(error);
    }
  };

  const onCloseButtonClick = useCallback(() => {
    const isDirty =
      isCancellationPolicyActive !==
        (orchidPro?.cancellation_policy_flat_fee !== null ||
          orchidPro?.cancellation_policy_percent_fee !== null) ||
      cancellationNoticeHours !== orchidPro?.cancellation_notice_hours ||
      (feeType === "flat" &&
        flatFee !== orchidPro?.cancellation_policy_flat_fee) ||
      (feeType === "percent" &&
        percentFee !== orchidPro?.cancellation_policy_percent_fee);

    if (!isDirty) {
      dispatch(hideMobileSidebar());
    } else {
      openConfirm();
    }
  }, [
    cancellationNoticeHours,
    dispatch,
    feeType,
    flatFee,
    orchidPro,
    percentFee,
    openConfirm,
    isCancellationPolicyActive,
  ]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <IconButton onClick={onCloseButtonClick}>
          <OutlineChevronLeft />
        </IconButton>
        Set Cancellation Policy
        <div style={{ visibility: "hidden" }}>
          <IconButton>
            <OutlineChevronLeft />
          </IconButton>
        </div>
      </div>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}
      <div className={styles.content}>
        <div>
          Sometimes your patients have to cancel the sessions.
          <br />
          Set a cancellation policy to find a balance between your flexibility
          and your schedule.
        </div>
        <RadioButton
          name="policy_active"
          label="No Cancellation Policy"
          checked={!isCancellationPolicyActive}
          onChange={() => setIsCancellationPolicyActive(false)}
        />
        <div>
          <RadioButton
            name="policy_active"
            label="Active Cancellation Policy"
            checked={isCancellationPolicyActive}
            onChange={() => setIsCancellationPolicyActive(true)}
          />

          {isCancellationPolicyActive && (
            <div className={styles["active-policy-details"]}>
              <div>
                <div>
                  <label className={styles.label}>
                    {messages.cancellationNotice}
                  </label>
                  <OverlayTrigger
                    placement={"top"}
                    overlay={
                      <Tooltip id="my-cancellation-notice-tooltip">
                        {messages.ifAPatientCancelsWithin}
                      </Tooltip>
                    }
                  >
                    <OutlineQuestionCircleSmall />
                  </OverlayTrigger>
                </div>
                <DropdownButton
                  variant="outline-secondary"
                  title={
                    <div className={styles["dropdown-title"]}>
                      {getNoticeHoursOptionText(cancellationNoticeHours)}
                      <ChevronDown />
                    </div>
                  }
                >
                  {NOTICE_HOURS_OPTIONS.map((hours) => (
                    <Dropdown.Item
                      key={hours}
                      onClick={() => {
                        setCancellationNoticeHours(hours);
                      }}
                    >
                      {getNoticeHoursOptionText(hours)}
                    </Dropdown.Item>
                  ))}
                </DropdownButton>
                <Button
                  variant="link"
                  className={styles["reset-button"]}
                  onClick={() =>
                    setCancellationNoticeHours(DEFAULT_NOTICE_HOURS)
                  }
                >
                  Reset to default
                </Button>
              </div>
              <div>
                <label className={styles.label}>
                  {messages.cancellationFee}
                </label>
                <InputGroup className="fee-option">
                  <DropdownButton
                    className={styles["fee-option-dropdown"]}
                    variant="outline-secondary"
                    title={
                      <div className={styles["dropdown-title"]}>
                        {feeType === "flat"
                          ? messages.flatFee
                          : messages.sessionRatePercentage}
                        <ChevronDown />
                      </div>
                    }
                  >
                    <Dropdown.Item onClick={() => setFeeType("flat")}>
                      {messages.flatFee}
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => setFeeType("percent")}>
                      {messages.sessionRatePercentage}
                    </Dropdown.Item>
                  </DropdownButton>
                  {feeType === "flat" && (
                    <>
                      <InputGroup.Text>$</InputGroup.Text>
                      <FormControl
                        type="number"
                        min={0}
                        max={10000}
                        value={flatFee}
                        onChange={(e) => {
                          setFlatFee(parseInt(e.target.value) ?? 0);
                        }}
                      />
                    </>
                  )}

                  {feeType === "percent" && (
                    <>
                      <FormControl
                        type="number"
                        min={0}
                        max={100}
                        value={percentFee}
                        onChange={(e) => {
                          setPercentFee(parseInt(e.target.value) ?? 0);
                        }}
                      />
                      <InputGroup.Text>%</InputGroup.Text>
                    </>
                  )}
                </InputGroup>
                <Button
                  variant="link"
                  className={styles["reset-button"]}
                  onClick={() => {
                    setFeeType(DEFAULT_FEE_TYPE);
                    setPercentFee(DEFAULT_PERCENT_FEE);
                  }}
                >
                  Reset to default
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          variant="primary"
          className={styles["apply-button"]}
          onClick={handleApply}
        >
          Save
        </Button>
      </div>

      <ConfirmModal
        show={showConfirm}
        onConfirm={() => {
          handleApply();
        }}
        onCancel={() => {
          closeConfirm();
          dispatch(hideMobileSidebar());
        }}
        labelTitle="Ready to update your cancellation policy?"
        labelCancel="No, Cancel"
        labelConfirm="Yes, Update"
      >
        The terms of your edited cancellation policy will only be applied to new
        sessions booked from this point onward. This update does not affect any
        existing sessions booked on your calendar.
      </ConfirmModal>
    </div>
  );
};

export default CancellationPolicyMobileScreen;
